type Point = { x: number; y: number };
type AngleType = 'minor' | 'reflex' | 'complete';

const EPSILON = 1e-6;

const parsePoint = (rawValue?: string): Point | null => {
  if (!rawValue) {
    return null;
  }

  const parts = rawValue.split(',').map((value) => Number(value.trim()));
  if (parts.length !== 2 || parts.some((value) => Number.isNaN(value))) {
    return null;
  }

  return { x: parts[0], y: parts[1] };
};

const normalize = (vector: Point): Point | null => {
  const magnitude = Math.hypot(vector.x, vector.y);
  if (magnitude < EPSILON) {
    return null;
  }

  return {
    x: vector.x / magnitude,
    y: vector.y / magnitude,
  };
};

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

const getDirectionToPoint = (from: Point, to: Point): Point | null => {
  return normalize({ x: to.x - from.x, y: to.y - from.y });
};

const getAngleType = (pathElement: SVGPathElement): AngleType => {
  const rawType = (pathElement.dataset.angleType || 'minor').trim().toLowerCase();
  if (rawType === 'reflex') {
    return 'reflex';
  }

  if (rawType === 'complete') {
    return 'complete';
  }

  return 'minor';
};

const getLabelTarget = (pathElement: SVGPathElement): SVGTextElement | null => {
  const labelId = pathElement.dataset.labelId?.trim();
  if (labelId) {
    const root = pathElement.ownerSVGElement;
    if (!root) {
      return null;
    }

    const byId = root.querySelector<SVGTextElement>(`#${CSS.escape(labelId)}`);
    if (byId) {
      return byId;
    }

    const byDataId = root.querySelector<SVGTextElement>(`.angle-label[data-label-id="${labelId}"], .angle-marker-label[data-label-id="${labelId}"]`);
    if (byDataId) {
      return byDataId;
    }
  }

  const nextSibling = pathElement.nextElementSibling;
  if (
    nextSibling instanceof SVGTextElement &&
    (nextSibling.classList.contains('angle-label') || nextSibling.classList.contains('angle-marker-label'))
  ) {
    return nextSibling;
  }

  return null;
};

const updateAngleMarkers = (container: ParentNode): void => {
  const anglePaths = container.querySelectorAll<SVGPathElement>(
    '.angle-marker, .angle-arc, .angle-interior, .angle-exterior',
  );

  anglePaths.forEach((pathElement) => {
    const vertex = parsePoint(pathElement.dataset.vertex);
    const previous = parsePoint(pathElement.dataset.prev);
    const next = parsePoint(pathElement.dataset.next);

    if (!vertex || !previous || !next) {
      return;
    }

    const u1 = getDirectionToPoint(vertex, previous);
    const u2 = getDirectionToPoint(vertex, next);

    if (!u1 || !u2) {
      return;
    }

    const edgeLength1 = Math.hypot(previous.x - vertex.x, previous.y - vertex.y);
    const edgeLength2 = Math.hypot(next.x - vertex.x, next.y - vertex.y);
    const localScale = Math.min(edgeLength1, edgeLength2);

    const d = clamp(localScale * 0.22, 10, 26);
    const k = clamp(localScale * 0.35, 16, 40);

    const arcStart: Point = {
      x: vertex.x + d * u1.x,
      y: vertex.y + d * u1.y,
    };

    const arcEnd: Point = {
      x: vertex.x + d * u2.x,
      y: vertex.y + d * u2.y,
    };

    const bisectorCandidate = normalize({
      x: u1.x + u2.x,
      y: u1.y + u2.y,
    });

    const cross = u1.x * u2.y - u1.y * u2.x;
    const fallbackDirection = normalize({
      x: -u1.y * (cross >= 0 ? 1 : -1),
      y: u1.x * (cross >= 0 ? 1 : -1),
    });

    const controlDirection = bisectorCandidate ?? fallbackDirection;
    if (!controlDirection) {
      return;
    }

    const controlPoint: Point = {
      x: vertex.x + k * controlDirection.x,
      y: vertex.y + k * controlDirection.y,
    };

    const angleType = getAngleType(pathElement);

    // Create arc path data
    let arcPathData = `M ${arcStart.x},${arcStart.y} Q ${controlPoint.x},${controlPoint.y} ${arcEnd.x},${arcEnd.y}`;
    let fillPathData = `M ${vertex.x},${vertex.y} L ${arcStart.x},${arcStart.y} Q ${controlPoint.x},${controlPoint.y} ${arcEnd.x},${arcEnd.y} Z`;

    if (angleType === 'reflex') {
      const reflexControlPoint: Point = {
        x: vertex.x - k * 1.6 * controlDirection.x,
        y: vertex.y - k * 1.6 * controlDirection.y,
      };

      arcPathData = `M ${arcStart.x},${arcStart.y} Q ${reflexControlPoint.x},${reflexControlPoint.y} ${arcEnd.x},${arcEnd.y}`;
      fillPathData = '';
    }

    if (angleType === 'complete') {
      const r = clamp(d + 4, 14, 30);
      arcPathData = `M ${vertex.x - r},${vertex.y} A ${r},${r} 0 1 0 ${vertex.x + r},${vertex.y} A ${r},${r} 0 1 0 ${vertex.x - r},${vertex.y}`;
      fillPathData = '';
    }
    
    pathElement.setAttribute('d', arcPathData);

    // Create filled wedge (if not already present)
    const existingFill = pathElement.previousElementSibling;
    const isFillAlreadyPresent = 
      existingFill instanceof SVGPathElement && 
      existingFill.classList.contains('angle-marker-fill');
    
    if (!isFillAlreadyPresent && fillPathData) {
      const fillPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      
      fillPath.setAttribute('d', fillPathData);
      fillPath.setAttribute('fill', 'rgba(139,90,43,0.12)');
      fillPath.setAttribute('stroke', 'none');
      fillPath.classList.add('angle-marker-fill');
      
      pathElement.parentNode?.insertBefore(fillPath, pathElement);
    }

    if (isFillAlreadyPresent && !fillPathData) {
      existingFill.remove();
    }

    const label = getLabelTarget(pathElement);
    if (label) {
      // Place label beyond the arc along the bisector so it never overlaps
      const labelDirection = angleType === 'reflex'
        ? { x: -controlDirection.x, y: -controlDirection.y }
        : controlDirection;
      const labelDistance = angleType === 'complete' ? d + 22 : k + 18;
      const labelX = vertex.x + labelDistance * labelDirection.x;
      const labelY = vertex.y + labelDistance * labelDirection.y;
      label.setAttribute('x', String(Math.round(labelX)));
      label.setAttribute('y', String(Math.round(labelY)));
    }
  });
};

export const enhanceQuestionSvg = (container: ParentNode): void => {
  updateAngleMarkers(container);
};
